
import { create } from 'zustand';
import { UserService } from '@/services/user.service';import { UserProfile, UserProfileEndParkingNotificationEnum, GarageAccount, JSONWebToken, UserMergeRequest, UserProfileConflictResponse, MonthlyAccount } from '@parkingbase/ecommerce-api-dev';
import { getGarageAccountTotalDue } from '@/lib/garage-utils';
import { useThemeStore } from '@/stores/theme.store';
import { clearAllGuestFields } from '@/lib/guest-field-memory';
import { logger } from '@/lib/logger';
import { getApiErrorStatus } from '@/lib/api-error-utils';
import { postMessageToNative } from '@/lib/webview-utils';
import { clearImpersonationSession } from '@/lib/impersonation';

export type DeleteAccountResult = {
  success: boolean;
  /** Non-zero only when deletion was blocked by linked monthly accounts. */
  linkedAccountCount: number;
};

type AuthState = {
  isAuthenticated: boolean;
  /**
   * True once auth hydration has run (HydrateStoreProvider), regardless of
   * whether a session was found. Lets consumers distinguish a genuine guest
   * (`isAuthInitialized && !isAuthenticated`) from the pre-hydration tick
   * where the store is still empty.
   */
  isAuthInitialized: boolean;
  /** True while this tab runs a back-office support session (`lib/impersonation.ts`). */
  isImpersonating: boolean;
  endImpersonation: () => Promise<void>;
  user: UserProfile | null;
  token: string | null;
  refreshToken: string | null;
  tokenDetails: JSONWebToken | null;
  garageAccounts: GarageAccount[];
  /** True while a `fetchGarageAccounts` request is in flight. */
  isLoadingGarageAccounts: boolean;
  /**
   * True once `fetchGarageAccounts` has completed at least once (success or
   * failure). Lets consumers distinguish "still fetching on cold mount" from
   * "fetched and genuinely empty", so a not-found / empty state is never shown
   * before the first fetch resolves (PB-3324).
   */
  hasLoadedGarageAccounts: boolean;
  /**
   * True when the last `fetchGarageAccounts` attempt failed. Deliberately separate from
   * `hasLoadedGarageAccounts`, which must still flip to `true` on failure so empty/not-found
   * states can render instead of an endless skeleton.
   *
   * Consumers that infer something from an *absence* in `garageAccounts` must check this
   * first: on failure the list is empty because the fetch died, not because the customer has
   * no accounts. Reading empty-as-fact is how a failed call silently granted an Introductory
   * Rate to customers who did not qualify (PB-2548).
   */
  garageAccountsLoadFailed: boolean;
  /**
   * Cached `/user/subaccounts` map (keyed by `monthlyAccount.documentId`). This is the
   * only endpoint that computes `statementBalance` for Primary Billing Accounts, so it
   * is fetched once per Manage-Permit visit (by the layout) and reused to enrich the
   * accounts list + permit-detail header + Subaccounts tab. (PB-3542)
   */
  subAccountsMap: Record<string, MonthlyAccount> | null;
  isLoadingSubAccountsMap: boolean;
  hasLoadedSubAccountsMap: boolean;
  /**
   * Fetches and caches the sub-accounts map. Returns the cache once loaded. Concurrent
   * callers share one in-flight request. Fail-soft: logs and returns {} on error.
   */
  fetchSubAccountsMap: () => Promise<Record<string, MonthlyAccount>>;
  initialize: () => Promise<void>;
  setRefreshToken: (refreshToken: string) => void;
  setRefreshTokenOnCookie: (refreshToken: string) => Promise<void>;
  isLoadingUser: boolean;
  setUser: (user: UserProfile | null) => void;
  setToken: (token: string | null) => void;
  fetchUser: () => Promise<void>;
  fetchGarageAccounts: () => Promise<void>;
  totalDue: () => number;
  logout: () => Promise<void>;
  updateNotificationPreferences: (
    notifications: UserProfileEndParkingNotificationEnum[]
  ) => Promise<void>;
  updateProfile: (data: Partial<UserProfile>) => Promise<boolean>;
  isUpdatingProfile: boolean;
  generateOtpForChangePhone: (phone: string) => Promise<boolean>;
  generateOtpForChangeEmail: (email: string) => Promise<boolean>;
  /**
   * Verifies the OTP and checks whether the new phone/email already belongs to
   * another account. Returns the conflict response (or null on request failure).
   * When no conflict is found the backend has already applied the change.
   */
  verifyOtpForChangeWithConflictCheck: (
    otp: string
  ) => Promise<UserProfileConflictResponse | null>;
  mergeProfiles: (request: UserMergeRequest) => Promise<boolean>;
  isGeneratingOtp: boolean;
  isVerifyingOtp: boolean;
  isMerging: boolean;
  otpError: string | null;
  clearOtpError: () => void;
  /**
   * Set when OTP-verify hits the backend's Rutgers phone-isolation hard block
   * (HTTP 409 `identifier_conflict`). The phone can't be merged, so the caller
   * must show an error instead of opening the merge prompt.
   */
  phoneConflictBlocked: boolean;
  clearPhoneConflict: () => void;
  mergeError: string | null;
  clearMergeError: () => void;
  profileError: string | null;
  clearProfileError: () => void;
  deleteAccount: () => Promise<DeleteAccountResult>;
  isDeletingAccount: boolean;
};

const userService = new UserService();

// Shared in-flight promise so concurrent callers of fetchSubAccountsMap collapse onto a
// single /user/subaccounts request. (PB-3542)
let subAccountsMapInFlight: Promise<Record<string, MonthlyAccount>> | null = null;

export const useAuthStore = create<AuthState>((set, get) => ({
  isAuthenticated: false,
  isAuthInitialized: false,
  isImpersonating: false,
  token: null,
  refreshToken: null,
  user: null,
  tokenDetails: null,
  garageAccounts: [],
  isLoadingGarageAccounts: false,
  hasLoadedGarageAccounts: false,
  garageAccountsLoadFailed: false,
  subAccountsMap: null,
  isLoadingSubAccountsMap: false,
  hasLoadedSubAccountsMap: false,
  isLoadingUser: false,
  isUpdatingProfile: false,
  isGeneratingOtp: false,
  isVerifyingOtp: false,
  isMerging: false,
  otpError: null,
  clearOtpError: () => set({ otpError: null }),
  phoneConflictBlocked: false,
  clearPhoneConflict: () => set({ phoneConflictBlocked: false }),
  mergeError: null,
  clearMergeError: () => set({ mergeError: null }),
  profileError: null,
  clearProfileError: () => set({ profileError: null }),
  isDeletingAccount: false,
  initialize: async () => {
    const userService = new UserService();
    const refreshToken = useAuthStore.getState().refreshToken;
    if (refreshToken) {
      const response = await userService.loginUsingRefreshToken(refreshToken);
      const composedToken = `${response.token_type} ${response.access_token}`;
      set({ tokenDetails: response });
      get().setToken(composedToken);
      await useAuthStore.getState().setRefreshTokenOnCookie(response.refresh_token);
    }
  },
  setRefreshToken: (refreshToken: string) => {
    set({ refreshToken });
  },
  // The access token is kept in-memory only (never localStorage). It is
  // short-lived and re-hydrated on every load from the httpOnly refresh-token
  // cookie via the SSR exchange in app/layout.tsx + HydrateStoreProvider.
  // Persisting it to localStorage left a stale copy that survived deploys and
  // client-state resets. (PB-3288)
  //
  // Setting a token also marks the user as loading: AppDataProvider fires
  // fetchUser() whenever a token appears, so `token present && user null` is a
  // loading state, not a failure. Keeping this invariant lets the app-layout
  // boot gate distinguish "still loading the profile" from "profile fetch
  // failed" without flashing a false error on client-side login. (PB-3288)
  setToken: (token: string | null) => {
    // Signing in discards the guest field store, across EVERY device (PB-4851). A profile is
    // authoritative, so the guest copy is now redundant PII — and a parker who used three zones
    // as a guest has three records, which is why this is a prefix sweep and not a single removal.
    //
    // Gated on a token ARRIVING, not on any change: `setToken(null)` is the logout path, and
    // clearing there would delete a guest’s values every time a session simply expired.
    if (token && !get().token) {
      clearAllGuestFields();
    }
    set({ token, isAuthenticated: !!token, isLoadingUser: !!token });
  },
  setUser: (user: UserProfile | null) =>
    set({
      user,
      isAuthenticated: !!user || !!get().token,
    }),
  fetchUser: async () => {
    set({ isLoadingUser: true });
    try {
      const userData = await userService.getUserProfile();
      set({ user: userData, isAuthenticated: true });
    } catch (error) {
      logger.error('Failed to fetch user profile', error, {
        feature: 'user-store',
        route: 'fetchUser',
      });
    } finally {
      set({ isLoadingUser: false });
    }
  },
  fetchGarageAccounts: async () => {
    set({ isLoadingGarageAccounts: true, garageAccountsLoadFailed: false });
    try {
      const garageAccounts = await userService.getGarageAccountByDomain();
      set({ garageAccounts, garageAccountsLoadFailed: false });
    } catch (error) {
      // Record the failure so callers can tell "no accounts" from "we never found out".
      set({ garageAccountsLoadFailed: true });
      logger.error('Failed to fetch garage accounts', error, {
        feature: 'user-store',
        route: 'fetchGarageAccounts',
      });
    } finally {
      set({ isLoadingGarageAccounts: false, hasLoadedGarageAccounts: true });
    }
  },
  fetchSubAccountsMap: async () => {
    if (get().hasLoadedSubAccountsMap) {
      return get().subAccountsMap ?? {};
    }
    if (subAccountsMapInFlight) {
      return subAccountsMapInFlight;
    }
    set({ isLoadingSubAccountsMap: true });
    subAccountsMapInFlight = (async () => {
      try {
        const map = await userService.getSubAccounts();
        set({ subAccountsMap: map, hasLoadedSubAccountsMap: true });
        return map;
      } catch (error) {
        // Fail-soft: a sub-accounts failure must not break Manage Permit — PBAs simply
        // keep their un-enriched statementBalance. Left unloaded so a later visit retries.
        logger.error('Failed to fetch sub-accounts map', error, {
          feature: 'user-store',
          route: 'fetchSubAccountsMap',
        });
        return {};
      } finally {
        set({ isLoadingSubAccountsMap: false });
        subAccountsMapInFlight = null;
      }
    })();
    return subAccountsMapInFlight;
  },
  totalDue: () => {
    const theme = useThemeStore.getState().theme;

    // If theme has not yet been loaded, we cannot accurately compute
    // any theme-dependent totals, so return 0 as a safe default.
    if (!theme) {
      return 0;
    }

    return get().garageAccounts.reduce((acc, account) => {
      if (account.status === 'ACTIVE') {
        return acc + getGarageAccountTotalDue(account, theme);
      }
      return acc + 0;
    }, 0);
  },
  setRefreshTokenOnCookie: async (refreshToken: string) => {
    // Guarded here, not at the call sites: `initialize()` also lands here after
    // a refresh, and that cookie would outlive the support popup (PB-3817).
    if (get().isImpersonating) return;

    try {
      const userService = new UserService();
      await userService.setRefreshTokenOnCookie(refreshToken);
      set({ refreshToken });
    } catch (error) {
      logger.error('Failed to persist refresh token to cookie', error, {
        feature: 'user-store',
        route: 'setRefreshTokenOnCookie',
      });
    }
  },

  endImpersonation: async () => {
    clearImpersonationSession();
    try {
      await new UserService().clearImpersonationMarker();
    } catch (error) {
      logger.error('Failed to clear impersonation marker', error, {
        feature: 'user-store',
        route: 'endImpersonation',
      });
    }
    get().setToken(null);
    subAccountsMapInFlight = null;
    set({
      isImpersonating: false,
      user: null,
      garageAccounts: [],
      hasLoadedGarageAccounts: false,
      subAccountsMap: null,
      hasLoadedSubAccountsMap: false,
      refreshToken: null,
      tokenDetails: null,
      isAuthenticated: false,
    });
  },

  /**
   * Ends the session — here, in the cookie, and at the identity server.
   *
   * ## Invariants:
   * - The revoke runs BEFORE the cookie is deleted. Why: the revoke route handler reads the
   *   refresh token out of that cookie, so deleting first would leave it nothing to revoke and
   *   the session would stay renewable. (PB-5476, AC 11)
   * - Both the automatic sign-out and the Log out button reach this method, which is why the
   *   revoke lives here and not at either call site — there are two of those
   *   (`useUserMenu.handleLogoutClick` and the account sidebar) and they drift.
   * - A failed revoke still signs the customer out locally. Why: leaving someone signed in
   *   because identity was unreachable is the worse outcome; `revokeRefreshToken` logs rather
   *   than throwing.
   * - The impersonation branch does not revoke. Why: a support session holds no refresh token
   *   and no cookie — there is nothing on the server to end.
   */
  logout: async () => {
    try {
      const userService = new UserService();
      // No refresh cookie to delete — clear the per-tab credential + marker instead.
      if (get().isImpersonating) {
        await get().endImpersonation();
        postMessageToNative({ type: 'logoutRequest' });
        return;
      }
      // Guarded a second time even though `revokeRefreshToken` swallows its own failures: if a
      // throw ever escapes it, the catch below would skip the local clear and leave the
      // customer believing they had signed out while still authenticated. Two layers, because
      // that failure is silent and severe.
      try {
        await userService.revokeRefreshToken();
      } catch (error) {
        logger.error('Token revocation failed during logout; signing out locally anyway', error, {
          feature: 'user-store',
          route: 'logout',
        });
      }
      await userService.deleteRefreshTokenFromCookie();
      get().setToken(null);
      // Clear the cached sub-accounts map so one user's billing data never leaks to the next. (PB-3542)
      subAccountsMapInFlight = null;
      set({
        user: null,
        garageAccounts: [],
        isLoadingGarageAccounts: false,
        hasLoadedGarageAccounts: false,
        garageAccountsLoadFailed: false,
        subAccountsMap: null,
        isLoadingSubAccountsMap: false,
        hasLoadedSubAccountsMap: false,
        refreshToken: null,
        tokenDetails: null,
        isAuthenticated: false,
      });
      // Notify the React Native shell so it can clear its own state/navigation
      postMessageToNative({ type: 'logoutRequest' });
    } catch (error) {
      logger.error('Failed to clear auth during logout', error, {
        feature: 'user-store',
        route: 'logout',
      });
    }
  },
  deleteAccount: async () => {
    set({ isDeletingAccount: true });
    try {
      await userService.deleteAccount();
      await get().logout();
      return { success: true, linkedAccountCount: 0 };
    } catch (error) {
      logger.error('Failed to delete account', error, {
        feature: 'user-store',
        route: 'deleteAccount',
      });
      // 400 = deletion blocked by linked monthly (garage) accounts.
      const isLinkedAccountsError = getApiErrorStatus(error) === 400;
      return {
        success: false,
        linkedAccountCount: isLinkedAccountsError ? get().garageAccounts.length : 0,
      };
    } finally {
      set({ isDeletingAccount: false });
    }
  },
  updateProfile: async (data: Partial<UserProfile>) => {
    const user = get().user;
    if (!user) return false;
    set({ isUpdatingProfile: true, profileError: null });
    try {
      await userService.updateProfile({ ...user, ...data });
      // API returns boolean, not the updated user — optimistically merge the changes
      set({ user: { ...user, ...data } });
      return true;
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      const message =
        axiosError?.response?.data?.message || 'Failed to update profile. Please try again.';
      set({ profileError: message });
      logger.error('Failed to update profile', error, {
        feature: 'user-store',
        route: 'updateProfile',
      });
      return false;
    } finally {
      set({ isUpdatingProfile: false });
    }
  },
  generateOtpForChangePhone: async (phone: string) => {
    const companyId = useThemeStore.getState().theme?.companyId;
    if (!companyId) return false;
    set({ isGeneratingOtp: true, otpError: null });
    try {
      await userService.generateOtpForChangeEmailPhone(companyId, phone);
      return true;
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      const message =
        axiosError?.response?.data?.message ||
        'Failed to send verification code. Please try again.';
      set({ otpError: message });
      logger.error('Failed to generate OTP', error, {
        feature: 'user-store',
        route: 'generateOtpForChangePhone',
      });
      return false;
    } finally {
      set({ isGeneratingOtp: false });
    }
  },
  generateOtpForChangeEmail: async (email: string) => {
    const companyId = useThemeStore.getState().theme?.companyId;
    if (!companyId) return false;
    set({ isGeneratingOtp: true, otpError: null });
    try {
      await userService.generateOtpForChangeEmailPhone(companyId, undefined, email);
      return true;
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      const message =
        axiosError?.response?.data?.message ||
        'Failed to send verification code. Please try again.';
      set({ otpError: message });
      logger.error('Failed to generate OTP', error, {
        feature: 'user-store',
        route: 'generateOtpForChangeEmail',
      });
      return false;
    } finally {
      set({ isGeneratingOtp: false });
    }
  },
  verifyOtpForChangeWithConflictCheck: async (otp: string) => {
    set({ isVerifyingOtp: true, otpError: null, phoneConflictBlocked: false });
    try {
      const result = await userService.verifyOtpForChangeEmailPhoneWithConflictCheck(otp);
      // success === false means the OTP itself was rejected (200 response, no throw).
      if (result && result.success === false) {
        set({ otpError: 'Verification failed. Please try again.' });
      }
      return result;
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      // A 409 identifier_conflict here is the backend's Rutgers phone-isolation hard block:
      // flag it (not a generic OTP error) so the caller shows an error rather than the merge prompt.
      if (getApiErrorStatus(error) === 409 && axiosError?.response?.data?.message === 'identifier_conflict') {
        set({ phoneConflictBlocked: true });
        return null;
      }
      const message =
        axiosError?.response?.data?.message || 'Verification failed. Please try again.';
      set({ otpError: message });
      logger.error('Failed to verify OTP with conflict check', error, {
        feature: 'user-store',
        route: 'verifyOtpForChangeWithConflictCheck',
      });
      return null;
    } finally {
      set({ isVerifyingOtp: false });
    }
  },
  mergeProfiles: async (request: UserMergeRequest) => {
    set({ isMerging: true, mergeError: null });
    try {
      await userService.mergeProfiles(request);
      return true;
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      const message =
        axiosError?.response?.data?.message ||
        "We couldn't merge your accounts. Please try again.";
      set({ mergeError: message });
      logger.error('Failed to merge profiles', error, {
        feature: 'user-store',
        route: 'mergeProfiles',
      });
      return false;
    } finally {
      set({ isMerging: false });
    }
  },
  updateNotificationPreferences: async (notifications: UserProfileEndParkingNotificationEnum[]) => {
    const user = get().user;
    if (!user) return;
    const previousNotifications = user.endParkingNotification;
    set({ user: { ...user, endParkingNotification: notifications } });
    try {
      await userService.updateProfile({ ...user, endParkingNotification: notifications });
    } catch (error) {
      set({ user: { ...user, endParkingNotification: previousNotifications } });
      logger.error('Failed to save notification preferences', error, {
        feature: 'user-store',
        route: 'updateNotificationPreferences',
      });
    }
  },
}));
